import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchUserSettings } from '../../features/userSettings/userSettingsSlice';
import { completeOnboarding, skipOnboarding } from '../../api/onboarding';
import MarketSelectionStep from './MarketSelectionStep';
import AssetPickerStep from './AssetPickerStep';
import SignalPreferencesStep from './SignalPreferencesStep';
import CompletionStep from './CompletionStep';
import DWLFLoader from '../DWLFLoader';

const STEPS = [
  'Market Selection',
  'Pick Assets',
  'Signal Preferences',
  'All Set!',
];

const OnboardingWizard = ({ open, onComplete }) => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Wizard state
  const [selectedMarkets, setSelectedMarkets] = useState(['crypto', 'stocks']);
  const [selectedAssets, setSelectedAssets] = useState([
    { symbol: 'BTC/USD', name: 'Bitcoin', market: 'crypto' },
    { symbol: 'AAPL', name: 'Apple', market: 'stocks' },
    { symbol: 'NVDA', name: 'NVIDIA', market: 'stocks' },
    { symbol: 'TSLA', name: 'Tesla', market: 'stocks' },
    { symbol: 'SPY', name: 'S&P 500 ETF', market: 'stocks' },
  ]);
  const [selectedSignals, setSelectedSignals] = useState(['momentum', 'trend']);

  const handleNext = () => {
    if (activeStep < STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    setError(null);

    try {
      // Prepare onboarding data
      const preferences = {
        markets: selectedMarkets,
        symbols: selectedAssets.map((a) => a.symbol),
        signalTypes: selectedSignals,
      };

      // Send to backend - this will:
      // 1. Set onboardingCompleted flag
      // 2. Populate watchlist with selected assets
      // 3. Enable pre-built events based on signal preferences
      await completeOnboarding(preferences);

      // Refresh user settings to get the updated onboardingCompleted flag
      await dispatch(fetchUserSettings({ force: true })).unwrap();

      setSaving(false);

      // Call completion callback (redirects to markets page)
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Failed to save onboarding preferences:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save preferences';
      setError(typeof err === 'string' ? err : errorMessage);
      setSaving(false);
    }
  };

  // Skip the wizard without provisioning a watchlist or enabling any
  // template events. Still flips the `onboardingCompleted` flag on the
  // backend so the wizard doesn't reappear on the next navigation.
  // Without this, users were trapped on every page with no escape.
  const handleSkip = async () => {
    setSaving(true);
    setError(null);

    try {
      await skipOnboarding();
      await dispatch(fetchUserSettings({ force: true })).unwrap();
      setSaving(false);
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Failed to skip onboarding:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to skip onboarding';
      setError(typeof err === 'string' ? err : errorMessage);
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <MarketSelectionStep
            selectedMarkets={selectedMarkets}
            onSelectMarkets={setSelectedMarkets}
          />
        );
      case 1:
        return (
          <AssetPickerStep
            selectedMarkets={selectedMarkets}
            selectedAssets={selectedAssets}
            onSelectAssets={setSelectedAssets}
          />
        );
      case 2:
        return (
          <SignalPreferencesStep
            selectedSignals={selectedSignals}
            onSelectSignals={setSelectedSignals}
          />
        );
      case 3:
        return (
          <CompletionStep
            selectedAssets={selectedAssets}
            selectedSignals={selectedSignals}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = activeStep === STEPS.length - 1;
  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedMarkets.length > 0;
      case 1:
        return selectedAssets.length > 0;
      case 2:
        return selectedSignals.length > 0;
      default:
        return true;
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          bgcolor: 'var(--dwlf-bg)',
          color: 'var(--dwlf-text)',
          border: '1px solid var(--dwlf-accent-20)',
          borderRadius: 0,
          boxShadow: '0 0 40px var(--dwlf-accent-10)',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Close (skip) button — positioned absolutely so it sits in the
            top-right of the dialog regardless of the header layout. */}
        <IconButton
          onClick={handleSkip}
          disabled={saving}
          aria-label="Skip onboarding"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
            color: 'var(--dwlf-ink-50)',
            '&:hover': {
              color: 'var(--dwlf-text)',
              backgroundColor: 'var(--dwlf-ink-05)',
            },
          }}
        >
          <Close sx={{ fontSize: '1.2rem' }} />
        </IconButton>

        {/* Header */}
        <Box
          sx={{
            p: 4,
            borderBottom: '1px solid var(--dwlf-ink-08)',
            background: 'linear-gradient(135deg, var(--dwlf-accent-05) 0%, rgba(0, 0, 0, 0) 100%)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 600,
              color: 'var(--dwlf-accent)',
              mb: 1,
              pr: 4, // leave room for the absolute-positioned close button
            }}
          >
            Welcome to DWLF
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'var(--dwlf-ink-70)',
              fontFamily: '"IBM Plex Mono", monospace',
            }}
          >
            Let's set up your trading workspace in a few quick steps
          </Typography>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            sx={{
              mt: 3,
              '& .MuiStepLabel-root': {
                color: 'var(--dwlf-ink-50)',
              },
              '& .MuiStepLabel-label': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: 'var(--dwlf-accent)',
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: 'var(--dwlf-up)',
              },
              '& .MuiStepIcon-root': {
                color: 'var(--dwlf-ink-10)',
                borderRadius: 0,
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: 'var(--dwlf-accent)',
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: 'var(--dwlf-up)',
              },
            }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <Box sx={{ p: 4, minHeight: 400 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          {error && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'var(--dwlf-down-10)',
                border: '1px solid var(--dwlf-down)',
                borderRadius: 0,
              }}
            >
              <Typography variant="body2" sx={{ color: 'var(--dwlf-down)' }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: '1px solid var(--dwlf-ink-08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'var(--dwlf-surface)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || saving}
              sx={{
                color: 'var(--dwlf-ink-70)',
                borderRadius: 0,
                border: '1px solid var(--dwlf-ink-20)',
                px: 3,
                '&:hover': {
                  bgcolor: 'var(--dwlf-ink-05)',
                  borderColor: 'var(--dwlf-ink-40)',
                },
                '&.Mui-disabled': {
                  color: 'var(--dwlf-ink-30)',
                  borderColor: 'var(--dwlf-ink-10)',
                },
              }}
            >
              Back
            </Button>
            {/* Text-link style skip — same handler as the X button.
                Quieter affordance than a primary button so it doesn't
                compete with Next/Get Started. */}
            <Button
              onClick={handleSkip}
              disabled={saving}
              sx={{
                color: 'var(--dwlf-ink-45)',
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                '&:hover': {
                  color: 'var(--dwlf-text)',
                  bgcolor: 'transparent',
                },
              }}
            >
              Skip for now
            </Button>
          </Box>

          <Button
            onClick={isLastStep ? handleComplete : handleNext}
            disabled={!canProceed() || saving}
            variant="contained"
            sx={{
              bgcolor: 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              borderRadius: 0,
              px: 4,
              fontWeight: 600,
              fontFamily: '"Space Mono", monospace',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              '&:hover': {
                bgcolor: 'var(--dwlf-accent-hi)',
              },
              '&.Mui-disabled': {
                bgcolor: 'var(--dwlf-accent-30)',
                color: 'var(--dwlf-bg-50)',
              },
            }}
          >
            {saving ? (
              <>
                <DWLFLoader size="tiny" showText={false} />
                Saving...
              </>
            ) : isLastStep ? (
              'Get Started'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
